import { showDeleteConfirmation } from '@modules/common';
import { switchMap, tap } from 'rxjs/operators';
import { BaseBloc } from '@core/utils/bloc';
import { Repository } from '@core/utils/repository';
import { AutomationResponse, AutomationId } from '@business/entities/automation';
import { handleMessage, requestMessage } from '@business/messages';
import { AutomationAPI } from '@business/api/automation';
import { Session } from '@modules/auth/session';

export class AutomationBloc extends BaseBloc {
  private readonly automationRepo = new Repository<AutomationResponse>({
    getItemId: automation => automation.id,
  });

  readonly automations$ = this.automationRepo.items$;
  readonly selectAutomation$ = this.automationRepo.selectItem;

  onReset() {
    this.automationRepo.reset();
  }

  fetchAutomations = () => {
    return AutomationAPI.fetchAutomations().pipe(this.automationRepo.ops.reset(), handleMessage({ error: requestMessage('fetch_companies_error') }));
  };

  fetchAutomation = (id: AutomationId) => {
    return AutomationAPI.fetchAutomation(id).pipe(
      this.automationRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_automations_error') }),
    );
  };

  createAutomation = (automation: Partial<AutomationResponse>) => {
    return AutomationAPI.createAutomation(automation).pipe(
      this.automationRepo.ops.addOne(item => item),
      handleMessage({
        type: requestMessage('create_automation'),
      }),
    );
  };

  updateAutomation = (id: AutomationId, automation: Partial<AutomationResponse>) => {
    return AutomationAPI.updateAutomation(id, automation).pipe(
      this.automationRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_automation'),
      }),
    );
  };

  deleteAutomation = (automationId: AutomationId) => {
    return showDeleteConfirmation('Delete Automation', 'Do you really want to remove this automation?').pipe(
      switchMap(() => AutomationAPI.deleteAutomation(automationId)),

      this.automationRepo.ops.removeOne(() => automationId),
      handleMessage({
        type: requestMessage('delete_automation'),
      }),
    );
  };
}
